import { useEffect, useState } from "react";
import { Banknote, CreditCard, Plus, Copy, Clock, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtTL } from "@/lib/api";

const CHIPS = [50, 100, 250, 500, 1000];

function IbanModal({ info, onClose }) {
  const [left, setLeft] = useState(info.window_min * 60);
  const [paid, setPaid] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  const m = Math.floor(left / 60), s = left % 60;
  const markPaid = async () => {
    try { await api.post(`/wallet/topup/${info.id}/paid`); setPaid(true); toast.success("Bildirildi! Admin onayı sonrası bakiye yüklenecek."); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
      <div className="bg-panel-card border border-panel-border w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 anim-fadeup" onClick={(e) => e.stopPropagation()} data-testid="iban-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg">Havale / EFT ile Ödeme</h3>
          <button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-lg card"><X size={18} /></button>
        </div>
        <div className="text-center mb-4">
          <div className="text-xs text-panel-muted">Yatırılacak Tutar</div>
          <div className="font-head text-3xl font-extrabold text-brand-500">{fmtTL(info.amount)}</div>
          <div className="mt-2 inline-flex items-center gap-2 bg-amber-500/15 text-amber-500 rounded-full px-3 py-1 text-sm font-semibold">
            <Clock size={14} /> {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
          </div>
        </div>
        <div className="space-y-2 mb-4">
          {info.ibans.map((b, i) => (
            <div key={i} className="rounded-xl bg-panel-input border border-panel-border p-3">
              <div className="text-xs text-panel-muted">{b.bank} · {b.name}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="font-mono text-sm">{b.iban}</span>
                <button onClick={() => { navigator.clipboard.writeText(b.iban.replace(/\s/g, "")); toast.success("IBAN kopyalandı"); }} className="text-brand-500"><Copy size={16} /></button>
              </div>
            </div>
          ))}
          <div className="rounded-xl bg-panel-input border border-panel-border p-3 flex items-center justify-between">
            <div><div className="text-xs text-panel-muted">Açıklama</div><span className="font-mono text-sm font-semibold">{info.ref_code}</span></div>
            <button onClick={() => { navigator.clipboard.writeText(info.ref_code); toast.success("Kopyalandı"); }} className="text-brand-500"><Copy size={16} /></button>
          </div>
        </div>
        {!paid ? (
          <button onClick={markPaid} data-testid="iban-mark-paid" className="btn-blue w-full rounded-xl h-12 font-semibold">Ödeme Yaptım</button>
        ) : (
          <div className="rounded-xl bg-emerald-500/15 text-emerald-500 p-3 text-center text-sm font-semibold flex items-center justify-center gap-2">
            <CheckCircle2 size={18} /> Ödemeniz onay bekliyor
          </div>
        )}
      </div>
    </div>
  );
}

export default function Wallet() {
  const [w, setW] = useState(null);
  const [tx, setTx] = useState([]);
  const [amount, setAmount] = useState(100);
  const [method, setMethod] = useState("iban");
  const [busy, setBusy] = useState(false);
  const [ibanInfo, setIbanInfo] = useState(null);

  const load = () => {
    api.get("/wallet").then(({ data }) => setW(data));
    api.get("/wallet/transactions").then(({ data }) => setTx(data.transactions || []));
  };
  useEffect(() => { load(); }, []);

  const createPayment = async () => {
    if (amount <= 0) return toast.error("Geçerli bir tutar girin");
    setBusy(true);
    try {
      if (method === "iban") {
        const { data } = await api.post("/wallet/topup/iban", { amount: Number(amount) });
        setIbanInfo(data);
      } else {
        const { data } = await api.post("/wallet/topup/shopier", { amount: Number(amount) });
        window.location.href = data.payment_url;
      }
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pct = w && w.next_tier ? Math.min(100, (w.total_deposit / w.next_tier.min) * 100) : 100;
  const badge = (st) => ({ done: ["Tamamlandı", "bg-emerald-500/15 text-emerald-500"], pending: ["Bekliyor", "bg-amber-500/15 text-amber-500"], waiting_confirm: ["Onay Bekliyor", "bg-amber-500/15 text-amber-500"], cancelled: ["İptal", "bg-red-500/15 text-red-500"] }[st] || [st, "bg-panel-input text-panel-muted"]);

  return (
    <div>
      {/* balance card */}
      <div className="brand-grad rounded-2xl p-6 text-white shadow-xl shadow-brand/20" data-testid="wallet-balance-card">
        <div className="text-white/80 text-sm">Cüzdan Bakiyesi</div>
        <div className="font-head text-4xl font-extrabold mt-1">{fmtTL(w?.balance || 0)}</div>
        <div className="mt-5 flex items-center justify-between text-sm">
          <span>Seviye: <strong>{w?.rank || "Bronz"}</strong></span>
          {w?.next_tier && <span className="text-white/80">{w.next_tier.name} için {fmtTL(w.next_tier.min - w.total_deposit)}</span>}
        </div>
        <div className="mt-2 h-2 rounded-full bg-white/20 overflow-hidden"><div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
      </div>

      {/* top-up */}
      <div className="card p-6 mt-5">
        <h2 className="font-head font-bold text-xl mb-4">Bakiye Yükle</h2>
        <div className="grid grid-cols-5 gap-2 mb-3">
          {CHIPS.map((c) => (
            <button key={c} onClick={() => setAmount(c)} data-testid={`chip-${c}`}
              className={`rounded-xl h-12 text-sm font-semibold border transition-colors ${Number(amount) === c ? "btn-blue border-transparent" : "card hover:border-brand-500"}`}>{c}₺</button>
          ))}
        </div>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="topup-amount"
          className="pinput w-full rounded-xl h-14 px-4 text-lg font-semibold outline-none focus:border-brand-500 mb-3" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button onClick={() => setMethod("iban")} data-testid="method-iban"
            className={`rounded-xl p-4 text-left border transition-colors ${method === "iban" ? "border-brand-500 bg-brand/5" : "card"}`}>
            <Banknote className="text-brand-500 mb-2" size={22} />
            <div className="font-semibold text-sm">Havale / EFT</div>
            <div className="text-xs text-panel-muted">Manuel IBAN</div>
          </button>
          <button onClick={() => setMethod("shopier")} data-testid="method-shopier"
            className={`rounded-xl p-4 text-left border transition-colors ${method === "shopier" ? "border-brand-500 bg-brand/5" : "card"}`}>
            <CreditCard className="text-brand-500 mb-2" size={22} />
            <div className="font-semibold text-sm">Shopier</div>
            <div className="text-xs text-panel-muted">Kart ile 7/24</div>
          </button>
        </div>
        <button onClick={createPayment} disabled={busy} data-testid="create-payment"
          className="btn-blue w-full rounded-xl h-13 py-3.5 font-semibold flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} Ödeme Oluştur
        </button>
      </div>

      {/* history */}
      <h3 className="font-head font-bold text-lg mt-7 mb-3">Yükleme Geçmişi</h3>
      <div className="space-y-2">
        {tx.map((t) => {
          const [lbl, cls] = badge(t.status);
          return (
            <div key={t.id} className="card p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{t.amount > 0 ? "+" : ""}{fmtTL(t.amount)}</div>
                <div className="text-xs text-panel-muted">{t.note || t.type} · {(t.created_at || "").slice(0, 10)}</div>
              </div>
              <span className={`text-xs font-semibold rounded-full px-3 py-1 ${cls}`}>{lbl}</span>
            </div>
          );
        })}
        {tx.length === 0 && <div className="text-center text-panel-muted py-8 text-sm">Henüz işlem yok.</div>}
      </div>

      {ibanInfo && <IbanModal info={ibanInfo} onClose={() => { setIbanInfo(null); load(); }} />}
    </div>
  );
}
